import type { ITenant } from 'src/pages/tenants/tenants.types';
import type { ITicket } from 'src/pages/tickets/tickets.types';
import type { IUserItem } from 'src/pages/setting/setting.types';
import type { CSATSurvey } from 'src/pages/csat/slice/csatTypes';
import type { IAccount } from 'src/pages/accounts/accounts.types';
import type { IContact } from 'src/pages/contacts/contacts.types';
import type {
  KnowledgeGap,
  ConversationLearning,
} from 'src/pages/automation/agents/slice/agentLearningApi';
import type {
  TeamList,
  AccountList,
  TicketsList,
  ChannelData,
  CustomLabelsList,
} from 'src/types/user';

import { useState } from 'react';
import { varAlpha } from 'minimal-shared/utils';
import { usePopover } from 'minimal-shared/hooks';

import { Tooltip, TableRow } from '@mui/material';

import { TableRowCell } from './table-row-cell';
import { TableRowActions } from './table-row-actions';
import { RowCellCheckbox } from './cells/row-cell-checkbox';

import type { TableHeadCellProps } from '../head/table-head-custom';

// ----------------------------------------------------------------------
type TabItem = {
  value: string;
  label: string;
  color: 'default' | 'success' | 'warning' | 'error' | 'info';
  count: number;
};
const baseTabs: Omit<TabItem, 'count'>[] = [
  { value: 'all', label: 'ALL', color: 'default' },
  { value: 'sent', label: 'SENT', color: 'info' },
  { value: 'delivered', label: 'DELIVERED', color: 'warning' },
  { value: 'read', label: 'SEEN', color: 'success' },
  { value: 'failed', label: 'FAILED', color: 'error' },
];

type Props = {
  rows:
    | AccountList[]
    | TicketsList[]
    | IUserItem[]
    | TeamList[]
    | CustomLabelsList[]
    | IAccount[]
    | IContact[]
    | ITenant[]
    | ConversationLearning[]
    | KnowledgeGap[]
    | CSATSurvey[];
  table: any;
  editHref: (id: string) => string;
  contactDetailHref?: (id: string) => string;
  accountDetailHref?: (id: string) => string;
  userDetailHref?: (id: string) => string;
  teamsDetailHref?: (id: string) => string;
  onDeleteRow: (id: string) => void;
  headCells: TableHeadCellProps[];
  selectRowPremission?: boolean;
  handleEditContact?: (id: string) => string | Promise<void>;
  handleDeleteRecord?: (id: string) => void;
  handleIVRConnection?: (id: string) => void;
  handleCustomiseDID?: (id: string) => void;
  isSelectRecordDialog?: boolean;
  setRecordInputHandler?: (row: any) => void;
  handleResendMail?: (id: string) => string;
  handlePreview?: (id: any) => void;
  playCallRecording?: (id: string) => void;
  t?: any;
  isCampaignTamplate?: boolean;
  isEdit?: boolean;
  isDelete?: boolean;
  currentlyPlayingId?: string | null;
  selectAllFilter?: any;
  moduleName?: string;
  handleLoginAsUser?: (row: any) => void;
  handleOpenConversation?: (row: any) => void;
  userRole?: string;
  isSupportReport?: boolean;
  handlePhoneNumberClick?: (number: any) => void;
  isTenantInvoice?: boolean;
  handleMarkAsRead?: (id: string) => void;
  tenantUserDetailsHref?: (tenantId: string, userId: string) => string;
  tenantInvoiceDetailsHref?: (tenantId: string, userId: string) => string;
  currentTenantId?: string;
  isSuperAdmin?: boolean;
  handleAddFriend?: (row: any) => void;
  handlePayNowButton?: (id: any) => void;
  invoiceLockedMessage?: string;
  handleApproveLearning?: (id: string) => void | Promise<void>;
  handleRejectLearning?: (id: string) => void | Promise<void>;
  handleResolveKnowledgeGap?: (id: string) => void | Promise<void>;
  isApprovingLearning?: boolean;
  isRejectingLearning?: boolean;
  isResolvingKnowledgeGap?: boolean;
  handleToggleStatus?: (row: any) => void;
  handleQuickEdit?: (row: any) => void;
  isRowInteractionLocked?: (row: any) => boolean;
  rowInteractionLockedTooltip?: string;
  getRowInteractionLockedTooltip?: (row: any) => string;
  handleRefresh?: (id: string) => void;
};

// Export Props type for use in orchestrator
export type { Props };

export function TableRowCustomImpl({
  rows,
  table,
  editHref,
  onDeleteRow,
  headCells,
  selectRowPremission,
  handleEditContact,
  handleDeleteRecord,
  handleCustomiseDID,
  handleIVRConnection,
  handlePreview,
  isSelectRecordDialog = false,
  setRecordInputHandler,
  handleResendMail,
  playCallRecording,
  t,
  isCampaignTamplate = false,
  isEdit = false,
  isDelete = false,
  currentlyPlayingId = null,
  selectAllFilter = false,
  moduleName,
  contactDetailHref,
  accountDetailHref,
  userDetailHref,
  teamsDetailHref,
  handleLoginAsUser,
  handleOpenConversation,
  userRole,
  isSupportReport,
  handlePhoneNumberClick,
  isTenantInvoice = false,
  handleMarkAsRead,
  tenantUserDetailsHref,
  tenantInvoiceDetailsHref,
  currentTenantId,
  isSuperAdmin,
  handleAddFriend,
  handlePayNowButton,
  invoiceLockedMessage = '',
  handleApproveLearning,
  handleRejectLearning,
  handleResolveKnowledgeGap,
  isApprovingLearning,
  isRejectingLearning,
  isResolvingKnowledgeGap,
  handleToggleStatus,
  handleQuickEdit,
  isRowInteractionLocked,
  rowInteractionLockedTooltip = '',
  getRowInteractionLockedTooltip,
  handleRefresh,
}: Props) {
  const menuActions = usePopover();
  const [selectedId, setSelectedId] = useState<any>('');
  const openinvoiceUtils = (event: React.MouseEvent<HTMLElement>, row: any) => {
    menuActions.onOpen(event);
    setSelectedId(row);
  };

  return (
    <>
      {rows.map(
        (
          row:
            | AccountList
            | TicketsList
            | IUserItem
            | TeamList
            | CustomLabelsList
            | ChannelData
            | IAccount
            | IContact
            | ITenant
            | ITicket
            | ConversationLearning
            | KnowledgeGap
            | CSATSurvey,
          index
        ) => {
          const interactionLocked = Boolean(isRowInteractionLocked?.(row));
          const lockMessage = interactionLocked
            ? (getRowInteractionLockedTooltip?.(row) ?? rowInteractionLockedTooltip ?? '')
            : '';
          return (
            <Tooltip
              key={row.id ?? index + 1}
              title={lockMessage}
              placement="top"
              disableHoverListener
              enterNextDelay={300}
            >
              <TableRow
                selected={'id' in row ? table.selected.includes(row.id) : false}
                onClick={() => {
                  if (interactionLocked) return;
                  if (isSelectRecordDialog) setRecordInputHandler?.(row);

                  if ('id' in row && isSelectRecordDialog) {
                    table.onSelectRow(row.id, false);
                  }
                }}
                sx={{
                  cursor: interactionLocked
                    ? 'not-allowed'
                    : isSelectRecordDialog && !isSupportReport
                      ? 'pointer'
                      : 'default',
                  ...(interactionLocked
                    ? {
                        opacity: 0.62,
                        bgcolor: (theme) => varAlpha(theme.vars.palette.grey['500Channel'], 0.08),
                        '&:hover': {
                          bgcolor: (theme) => varAlpha(theme.vars.palette.grey['500Channel'], 0.12),
                          transform: 'none',
                          boxShadow: 'none',
                        },
                      }
                    : {
                        '&:hover': {
                          backgroundColor: 'action.hover',
                          transform: 'translateY(-2px)',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                        },
                      }),
                  // Animation stagger children
                  animation: 'fadeInUp 0.5s ease-out both',
                  animationDelay: `${index * 0.05}s`,
                  '@keyframes fadeInUp': {
                    '0%': {
                      opacity: 0,
                      transform: 'translateY(20px)',
                    },
                    '100%': {
                      opacity: 1,
                      transform: 'translateY(0)',
                    },
                  },
                  // Hover effects
                  transition: 'all 0.2s ease-in-out',
                }}
              >
                <RowCellCheckbox
                  selectRowPremission={selectRowPremission}
                  row={row}
                  table={table}
                  isCampaignTamplate={!!isCampaignTamplate}
                  interactionLocked={interactionLocked}
                />

                {headCells.map((headCell, headerIndex) => (
                  <TableRowCell
                    key={headCell.id || headerIndex}
                    row={row}
                    headCell={headCell}
                    headerIndex={headerIndex}
                    editHref={editHref}
                    contactDetailHref={contactDetailHref}
                    accountDetailHref={accountDetailHref}
                    isSelectRecordDialog={isSelectRecordDialog}
                    isSupportReport={isSupportReport}
                    t={t}
                    moduleName={moduleName}
                    isTenantInvoice={isTenantInvoice}
                    tenantInvoiceDetailsHref={tenantInvoiceDetailsHref}
                    currentTenantId={currentTenantId}
                    isSuperAdmin={isSuperAdmin}
                    tenantUserDetailsHref={tenantUserDetailsHref}
                    handleAddFriend={handleAddFriend}
                    handleCustomiseDID={handleCustomiseDID}
                    handleIVRConnection={handleIVRConnection}
                    handleEditContact={handleEditContact}
                    handleDeleteRecord={handleDeleteRecord}
                    isEdit={isEdit}
                    isDelete={isDelete}
                    currentlyPlayingId={currentlyPlayingId}
                    playCallRecording={playCallRecording}
                    handlePhoneNumberClick={handlePhoneNumberClick}
                    handleResendMail={handleResendMail}
                    handleLoginAsUser={handleLoginAsUser}
                    handlePreview={handlePreview}
                    handleRefresh={handleRefresh}
                    isCampaignTamplate={!!isCampaignTamplate}
                    handlePayNowButton={handlePayNowButton}
                    invoiceLockedMessage={invoiceLockedMessage}
                    handleApproveLearning={handleApproveLearning}
                    handleRejectLearning={handleRejectLearning}
                    handleResolveKnowledgeGap={handleResolveKnowledgeGap}
                    isApprovingLearning={isApprovingLearning}
                    isRejectingLearning={isRejectingLearning}
                    isResolvingKnowledgeGap={isResolvingKnowledgeGap}
                    handleToggleStatus={handleToggleStatus}
                    handleQuickEdit={handleQuickEdit}
                    menuActions={menuActions}
                    openinvoiceUtils={openinvoiceUtils}
                    handleOpenConversation={handleOpenConversation}
                    userRole={userRole}
                    baseTabs={baseTabs}
                    rowInteractionLocked={interactionLocked}
                    rowInteractionLockedMessage={lockMessage}
                  />
                ))}
              </TableRow>
            </Tooltip>
          );
        }
      )}

      <TableRowActions
        menuActions={menuActions}
        isTenantInvoice={isTenantInvoice}
        selectedId={selectedId}
        currentTenantId={currentTenantId}
        handleMarkAsRead={handleMarkAsRead}
        invoiceLockedMessage={invoiceLockedMessage}
        t={t}
      />
    </>
  );
}
